import { CurrencyPipe } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';



import { FormGroup, FormBuilder, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
// Zard UI
import { ZardInputDirective } from '@shared/components/ui/input/input.directive';
import { ZardSwitchComponent } from '@shared/components/ui/switch/switch.component';
import { ZardSelectComponent } from '@shared/components/ui/select/select.component';
import { ZardSelectItemComponent } from '@shared/components/ui/select/select-item.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { PlayerService } from 'app/modules/players/services/player/player.service';
import { PlayerBonusService } from 'app/modules/players/services/player-bonus/player-bonus.service';
import { PlayerBonusDialogComponent } from 'app/shared/components/player-bonus-dialog/player-bonus-dialog.component';
import { CustomFormValidator, getErrorMessage } from 'app/shared/utils/form-validator/form-validator.util';
import { ZardConfirmationService } from 'app/shared/services/zard-confirmation';
import { AFFILIATES } from 'app/shared/shared.types';
import { ZardDatePickerComponent } from '../ui/date-picker/date-picker.component';
import { ZardCardComponent } from '../ui/card/card.component';

@Component({
  selector: 'app-player-card',
  templateUrl: './player-card.component.html',
  styleUrls: ['./player-card.component.scss'],
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    TranslocoPipe,
    ZardInputDirective,
    ZardSwitchComponent,
    ZardSelectComponent,
    ZardSelectItemComponent,
    ReactiveFormsModule,
    CurrencyPipe,
    ZardDatePickerComponent,
    ZardCardComponent,
  ],
})
export class PlayerCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() player: any;
  @Input() playerId: number;

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  
  financial_details: any = null;
  generalData: any[] = [];
  playerForm: FormGroup;
  editMode: boolean = false;
  private originalFormValue: any;


  // Direct access to financial data - NO ARRAYS
  sportsFinancialDetails: any = null;
  casinoFinancialDetails: any = null;
  overallFinancialDetails: any = null;

  // Helper method to format currency values
  formatCurrency(value: number | null | undefined): string {
    if (value == null || value === undefined) {
      return '—';
    }
    try {
      if (value === 0) {
        return '0$';
      }
      return value.toFixed(2).replace(/\.?0+$/, '') + '$';
    } catch (error) {
      console.error('Error formatting currency:', error);
      return '—';
    }
  }

  // Services
  private _dialog = inject(MatDialog);
  private _playerService = inject(PlayerService);
  private _formBuilder = inject(FormBuilder);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _translocoService = inject(TranslocoService);
  private _snackBar = inject(MatSnackBar);
  private _playerBonusService = inject(PlayerBonusService);
  private _confirmation = inject(ZardConfirmationService);

  get isTestAccount(): boolean {
    return !!this.player?.testAccount;
  }

  ngOnInit(): void {
    try {
      if (this.player) {
        this.initializeForm(this.player);
        this.buildGeneralData();
        this.subscribeToFormChanges();
      }
      if (this.playerId) {
        this.getFinancialData();
      }
    } catch (error) {
      console.error('Error in ngOnInit:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['player'] && changes['player'].currentValue) {
        this.initializeForm(changes['player'].currentValue);
        this.buildGeneralData();
        this.subscribeToFormChanges();
      }
      // Only fetch financial data if playerId actually changed to a different value
      if (changes['playerId'] && !changes['playerId'].isFirstChange() && changes['playerId'].currentValue) {
        const currentPlayerId = changes['playerId'].currentValue;
        const previousPlayerId = changes['playerId'].previousValue;
        
        // Only fetch if playerId actually changed to a different non-undefined value
        if (currentPlayerId && currentPlayerId !== previousPlayerId) {
          // Reset financial_details to allow refetch for new player
          this.financial_details = null;
          this.getFinancialData();
        }
      }
    } catch (error) {
      console.error('Error in ngOnChanges:', error);
    }
  }

  markAsTestAccount(): void {
    if (!this.playerId) return;
    this._confirmation
      .open({
        title: this._translocoService.translate('navigation.players.mark_test.title') || 'Mark as Test Account',
        message: this._translocoService.translate('navigation.players.mark_test.message') || 'Are you sure you want to mark this account as a test account?',
        actions: {
          confirm: { show: true, label: this._translocoService.translate('actions.continue') || 'Continue', color: 'warn' },
          cancel: { show: true, label: this._translocoService.translate('actions.cancel') || 'Cancel' },
        },
        dismissible: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result || (result as any).confirmed !== true) return;
        this._playerService.markAsTestAccount(this.playerId).subscribe({
          next: () => {
            if (this.player) {
              this.player.testAccount = true;
            }
            this._snackBar.open(
              this._translocoService.translate('common.success'),
              this._translocoService.translate('actions.close'),
              { duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' }
            );
            this._changeDetectorRef.markForCheck();
          },
          error: () => {
            this._snackBar.open(
              this._translocoService.translate('common.error-occurred'),
              this._translocoService.translate('actions.close'),
              { duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' }
            );
          }
        });
      });
  }

  unmarkAsTestAccount(): void {
    if (!this.playerId) return;
    this._confirmation
      .open({
        title: this._translocoService.translate('navigation.players.unmark_test.title') || 'Unmark Test Account',
        message: this._translocoService.translate('navigation.players.unmark_test.message') || 'Are you sure you want to unmark this account as a test account?',
        actions: {
          confirm: { show: true, label: this._translocoService.translate('actions.continue') || 'Continue', color: 'warn' },
          cancel: { show: true, label: this._translocoService.translate('actions.cancel') || 'Cancel' },
        },
        dismissible: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result || (result as any).confirmed !== true) return;
        this._playerService.unmarkAsTestAccount(this.playerId).subscribe({
          next: () => {
            if (this.player) {
              this.player.testAccount = false;
            }
            this._snackBar.open(
              this._translocoService.translate('common.success'),
              this._translocoService.translate('actions.close'),
              { duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' }
            );
            this._changeDetectorRef.markForCheck();
          },
          error: () => {
            this._snackBar.open(
              this._translocoService.translate('common.error-occurred'),
              this._translocoService.translate('actions.close'),
              { duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' }
            );
          }
        });
      });
  }

  ngOnDestroy(): void {
    try {
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
    } catch (error) {
      console.error('Error in ngOnDestroy:', error);
    }
  }

  initializeForm(user: any | null): void {
    try {
      this.playerForm = this._formBuilder.group({
        name: [
          { value: user?.name || '', disabled: !this.editMode },
          [CustomFormValidator.required('Name is required')]
        ],
        surname: [
          { value: user?.surname || '', disabled: !this.editMode },
          [CustomFormValidator.required('Surname is required')]
        ],
        email: [
          { value: user?.email || '', disabled: !this.editMode },
          [CustomFormValidator.required('Email required'), CustomFormValidator.email('Invalid email')]
        ],
        importantNote: [{ value: user?.importantNote || '', disabled: !this.editMode }],
        dateOfBirth: [{ value: this.parseDate(user?.dateOfBirth) || null, disabled: !this.editMode }],
        gender: [{ value: user?.gender === true || user?.gender === 'true' ? 'true' : user?.gender === false || user?.gender === 'false' ? 'false' : null, disabled: !this.editMode }],
        favouriteTeam: [{ value: user?.favouriteTeam || null, disabled: !this.editMode }],
        street: [{ value: user?.street || null, disabled: !this.editMode }],
        city: [{ value: user?.city || null, disabled: !this.editMode }],
        postalCode: [{ value: user?.postalCode || null, disabled: !this.editMode }],
        country: [{ value: user?.country || '', disabled: !this.editMode }],
        prefix: [{ value: user?.prefix || '+1', disabled: !this.editMode }],
        number: [
          { value: user?.number || null, disabled: !this.editMode },
          [CustomFormValidator.required('Phone required'), CustomFormValidator.phoneNumber('Invalid phone')]
        ],
        username: [
          { value: user?.username || null, disabled: !this.editMode },
          [CustomFormValidator.required('Username required')]
        ],
        promoCode: [{ value: user?.promoCode || '', disabled: !this.editMode }],
        nationality: [{ value: user?.nationality || null, disabled: !this.editMode }],
        documentNumber: [{ value: user?.documentNumber || null, disabled: !this.editMode }],
        receiveOfferAndBonuses: [{ value: user?.receiveOfferAndBonuses || false, disabled: !this.editMode }],
        twoFactorAuth: [{ value: user?.twoFactorAuth || false, disabled: !this.editMode}],
        twoFactorWithdraw: [{ value: user?.twoFactorWithdraw || false, disabled: !this.editMode}],
        emailVerified: [{ value: user?.emailVerified || false, disabled: !this.editMode }],
        accountVerified: [{ value: user?.accountVerified || false, disabled: !this.editMode }],
        phoneNoVerified: [{ value: user?.phoneNoVerified, disabled: !this.editMode }],
        receiveNews: [{ value: user?.receiveNews || false, disabled: !this.editMode }],
        contactByMail: [{ value: user?.contactByMail || false, disabled: !this.editMode }],
        contactByPhone: [{ value: user?.contactByPhone || false, disabled: !this.editMode }],
        contactBySms: [{ value: user?.contactBySms || false, disabled: !this.editMode }],
        creationDate: [{ value: user?.creationDate || false, disabled: true }],
        lastLoginDate: [{ value: user?.lastLoginDate || false, disabled: true }],
        affiliateId: [{ value: user?.affiliateId || false, disabled: true }],
        referralUrl: [{ value: user?.referralUrl || false, disabled: true }],
        // New editable General Data fields
        verificationStatus: [{ value: user?.verificationStatus || 'PENDING', disabled: !this.editMode }],
        bonusAllowed: [{ value: user?.bonusAllowed || false, disabled: !this.editMode }],
        withdrawalAllowed: [{ value: user?.withdrawalAllowed || false, disabled: !this.editMode }],
        language: [{ value: user?.language || 'ENGLISH', disabled: !this.editMode }],
        vipLevel: [{ value: user?.vipLevel || 'LEVEL_0', disabled: !this.editMode }],
      });
      
      this.stripPrefixIfNeeded();
      
      // Add validators for prefix/number coupling
      this.addPhoneValidators();
    } catch (error) {
      console.error('Error initializing form:', error);
    }
  }

  private addPhoneValidators(): void {
    if (!this.playerForm) return;
    
    const prefixControl = this.playerForm.get('prefix');
    const numberControl = this.playerForm.get('number');
    
    if (prefixControl && numberControl) {
      // Subscribe to changes in both fields
      prefixControl.valueChanges.subscribe(() => {
        this.validatePhoneFields();
      });
      
      numberControl.valueChanges.subscribe(() => {
        this.validatePhoneFields();
      });
      
      // Initial validation
      this.validatePhoneFields();
    }
  }

  private validatePhoneFields(): void {
    if (!this.playerForm) return;
    
    const prefixControl = this.playerForm.get('prefix');
    const numberControl = this.playerForm.get('number');
    
    const prefixValue = prefixControl?.value;
    const numberValue = numberControl?.value;
    
    // If number is provided but prefix is empty
    if (numberValue && (!prefixValue || prefixValue.trim() === '')) {
      prefixControl?.setErrors({ required: 'Prefix is required when phone number is provided' });
    } else {
      prefixControl?.setErrors(null);
    }
    
    // If prefix is provided but number is empty
    if (prefixValue && (!numberValue || numberValue.trim() === '')) {
      numberControl?.setErrors({ required: 'Phone number is required when prefix is provided' });
    } else if (numberValue && prefixValue) {
      // Clear the error if both have values
      numberControl?.setErrors(null);
    }
  }

  toggleEditMode(): void {
    if (!this.playerForm) return;
    
    if (!this.editMode) {
      // Save a clone of the current form value preserving Date instances
      this.originalFormValue = this.cloneFormValuePreservingDates();
      this.editMode = true;
      // Enable all controls (except 2FA fields)
      Object.keys(this.playerForm.controls).forEach((field) => {
          this.playerForm.get(field).enable();
      });
    } else {
      // Cancel: revert form values.
      this.playerForm.reset(this.originalFormValue);
      this.editMode = false;
      // Disable controls again.
      Object.keys(this.playerForm.controls).forEach((field) => {
          this.playerForm.get(field).disable();
      });
      // Rebuild general data to reflect the reverted values
      this.buildGeneralData();
    }
    // Rebuild general data to reflect current form state
    this.buildGeneralData();
    this._changeDetectorRef.markForCheck();
  }

  savePlayer(): void {
    if (!this.playerForm || !this.player) return;
    
    let playerData: any;
    
    if (this.editMode && this.playerId) {
      // For updates, only send changed fields
      playerData = this.getChangedFields();
      
      // If no fields changed, just return without sending anything
      if (Object.keys(playerData).length === 0) {
        return;
      }
    } else {
      // For new players, send all fields
      playerData = this.playerForm.getRawValue();
      // Remove password if it's not needed
      delete playerData.password;
    }
    
    // Convert gender string back to boolean for backend
    if (playerData.gender !== undefined) {
      playerData.gender = playerData.gender === 'true' || playerData.gender === true;
    }
    
    // Format dates to send only date without time (YYYY-MM-DD format)
    if (playerData.dateOfBirth && playerData.dateOfBirth instanceof Date) {
      const year = playerData.dateOfBirth.getFullYear();
      const month = String(playerData.dateOfBirth.getMonth() + 1).padStart(2, '0');
      const day = String(playerData.dateOfBirth.getDate()).padStart(2, '0');
      playerData.dateOfBirth = `${year}-${month}-${day}`;
    }
    
    // Combine prefix and number if either is changed
    if (playerData.prefix !== undefined || playerData.number !== undefined) {
      const prefix = playerData.prefix || this.playerForm?.get('prefix')?.value || '+1';
      const number = playerData.number || this.playerForm?.get('number')?.value || '';
      
      // Only send if both are present
      if (prefix && number) {
        playerData.number = prefix + number;
      }
      // Remove prefix from payload - backend expects combined value in number field
      delete playerData.prefix;
    }
    
    const subscribe = {
      next: () => {
        this._snackBar.open(
          this._translocoService.translate(`players.${this.editMode ? 'updated' : 'created'}`),
          this._translocoService.translate('actions.close'),
          {
            duration: 3000,
            verticalPosition: 'top',
            horizontalPosition: 'right',
          }
        );
        // After saving, update the player object with the new values and exit edit mode
        if (this.editMode && this.playerForm && this.player) {
          const formValues = this.playerForm.getRawValue();
          // Convert gender back to string for form consistency
          if (formValues.gender !== undefined) {
            formValues.gender = formValues.gender === true ? 'true' : (formValues.gender === false ? 'false' : null);
          }
          Object.assign(this.player, formValues);
          // Update the original value to the new saved values
          this.originalFormValue = this.cloneFormValuePreservingDates();
          this.editMode = false;
          // Disable all controls again
          Object.keys(this.playerForm.controls).forEach((field) => {
            this.playerForm.get(field).disable();
          });
          // Rebuild general data to reflect the updated values
          this.buildGeneralData();
        }
        // Mark the form as pristine
        if (this.playerForm) {
          this.playerForm.markAsPristine();
          this.playerForm.markAsUntouched();
        }
        this._changeDetectorRef.markForCheck();
      },
      error: (error) => {
        if (error?.error?.violations && this.playerForm) {
          error.error.violations.forEach((violation) => {
            const control = this.playerForm.get(violation.fieldName);
            if (control) {
              control.setErrors({ serverError: violation.message });
              control.markAsTouched();
            }
          });
          this._changeDetectorRef.markForCheck();
        } else {
          this._snackBar.open(
            this._translocoService.translate('common.error-occurred'),
            'Close',
            {
              duration: 3000,
              verticalPosition: 'top',
              horizontalPosition: 'right',
            }
          );
        }
        console.error(error);
      },
    };

    // Remove password on update if needed.
    if (this.editMode && this.playerId) {
      this._playerService.update(this.playerId, playerData).subscribe(subscribe);
    } else {
      this._playerService.save(playerData).subscribe(subscribe);
    }
  }

  hasChanges(): boolean {
    return Object.keys(this.getChangedFields()).length > 0;
  }

  private getChangedFields(): any {
    if (!this.playerForm || !this.originalFormValue) return {};
    
    const currentValue = this.playerForm.getRawValue();
    const changedFields: any = {};
    
    // Fields that should never be sent to the backend (readonly fields)
    const readonlyFields = ['creationDate', 'lastLoginDate', 'affiliateId', 'referralUrl'];
    
    // Compare each field and only include changed fields
    Object.keys(currentValue).forEach((key) => {
      // Skip readonly fields - they shouldn't be sent to the backend
      if (readonlyFields.includes(key)) {
        return;
      }
      
      const currentFieldValue = currentValue[key];
      const originalFieldValue = this.originalFormValue[key];
      
      // Handle dates comparison with preserved Date instances
      let hasChanged = false;
      if (currentFieldValue instanceof Date || originalFieldValue instanceof Date) {
        const currentTime = currentFieldValue instanceof Date ? currentFieldValue.getTime() : new Date(currentFieldValue ?? '').getTime();
        const originalTime = originalFieldValue instanceof Date ? originalFieldValue.getTime() : new Date(originalFieldValue ?? '').getTime();
        hasChanged = currentTime !== originalTime;
      } else if (currentFieldValue == null && originalFieldValue == null) {
        // Both are null/undefined
        hasChanged = false;
      } else {
        // Simple value comparison (handles strings, numbers, booleans, etc.)
        hasChanged = currentFieldValue !== originalFieldValue;
      }
      
      if (hasChanged) {
        changedFields[key] = currentFieldValue;
      }
    });
    
    return changedFields;
  }

  private cloneFormValuePreservingDates(): any {
    if (!this.playerForm) return {};
    const raw = this.playerForm.getRawValue();
    const clone: any = Array.isArray(raw) ? [] : {};
    Object.keys(raw).forEach((key) => {
      const value = raw[key];
      if (value instanceof Date) {
        clone[key] = new Date(value.getTime());
      } else if (value && typeof value === 'object') {
        // Shallow copy objects (our form values are primitives or Date)
        clone[key] = { ...value };
      } else {
        clone[key] = value;
      }
    });
    return clone;
  }

  private stripPrefixIfNeeded(): void {
    if (!this.playerForm) return;
    const prefix: string = this.playerForm.value.prefix ?? '';
    let number: string = this.playerForm.value.number ?? '';
    if (!prefix || !number) return;
    const trimmedPrefix = prefix.trim();
    let trimmedNumber = number.trim();
    if (trimmedNumber.startsWith(trimmedPrefix)) {
      trimmedNumber = trimmedNumber.slice(trimmedPrefix.length).trim();
    }
    this.playerForm.patchValue({ number: trimmedNumber });
  }

  private subscribeToFormChanges(): void {
    if (!this.playerForm) return;
    
    // Subscribe to changes in the General Data fields to update the display in real-time
    this.playerForm.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Only rebuild general data if we're in edit mode to avoid unnecessary updates
        if (this.editMode && this.playerForm && this.player) {
          try {
            this.buildGeneralData();
            this._changeDetectorRef.markForCheck();
          } catch (error) {
            console.error('Error updating general data:', error);
          }
        }
      });
  }

  getErrorMessage(control: AbstractControl): string {
    if (!control) return '';
    if (control.hasError('apiError')) {
      return control.getError('apiError');
    }
    return getErrorMessage(control);
  }

  getFinancialData(): void {
    if (!this.playerId) return;
    if (!this.financial_details) {
      this._playerService.getFinancialDetails(this.playerId).subscribe({
        next: (data) => {
          this.financial_details = data;
          this.buildFinancialData();
        },
        error: (error) => {
          console.error('Error fetching financial data:', error);
        }
      });
    }
  }

  buildFinancialData(): void {
    if (!this.financial_details) return;
    
    try {
      // Direct assignment - NO ARRAYS, NO DESTRUCTURING
      this.sportsFinancialDetails = this.financial_details.sportsFinancialDetails;
      this.casinoFinancialDetails = this.financial_details.casinoFinancialDetails;
      this.overallFinancialDetails = this.financial_details.overallFinancialDetails;
      
      // Force change detection to ensure UI updates
      this._changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('Error building financial data:', error);
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return '—';
        const formattedDay = date.getDate().toString().padStart(2, '0');
        const formattedMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        const formattedYear = date.getFullYear();
        return `${formattedDay}-${formattedMonth}-${formattedYear}`;
      } else {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '—';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return '—';
    }
  }

  buildGeneralData(): void {
    if (!this.player || !this.playerForm) return;
    
    try {
      this.generalData = [
        { label: this._translocoService.translate('players.documents.general_data.account_verified'), value: this.playerForm.get('accountVerified')?.value !== null && this.playerForm.get('accountVerified')?.value !== undefined ? (this.playerForm.get('accountVerified')?.value ? this._translocoService.translate('common.yes') : this._translocoService.translate('common.no')) : '—', icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.verification_status'), value: this.getVerificationStatusDisplay(), icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.bonus_allowed'), value: this.playerForm.get('bonusAllowed')?.value ? this._translocoService.translate('common.yes') : this._translocoService.translate('common.no'), icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.withdraw_allowed'), value: this.playerForm.get('withdrawalAllowed')?.value ? this._translocoService.translate('common.yes') : this._translocoService.translate('common.no'), icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.language'), value: this.getLanguageDisplay(), icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.account_status'), value: this.player.accountStatus || '—', icon: '' },
        { label: this._translocoService.translate('players.documents.general_data.vip_level'), value: this.getVipLevelDisplay(), icon: '' },
      ];
    } catch (error) {
      console.error('Error building general data:', error);
      this.generalData = [];
    }
  }

  getVerificationStatusDisplay(): string {
    if (!this.playerForm || !this.player) return '—';
    const verificationStatus = this.playerForm.get('verificationStatus')?.value || this.player.verificationStatus;
    if (!verificationStatus) return '—';
    const verificationStatusKey = `players.documents.general_data.verification_statuses.${verificationStatus}`;
    const translated = this._translocoService.translate(verificationStatusKey);
    return translated === verificationStatusKey ? verificationStatus : translated;
  }

  getLanguageDisplay(): string {
    if (!this.playerForm || !this.player) return '—';
    const language = this.playerForm.get('language')?.value || this.player.language;
    if (!language) return '—';
    const languageKey = `players.documents.general_data.languages.${language}`;
    const translated = this._translocoService.translate(languageKey);
    return translated === languageKey ? language : translated;
  }

  getVipLevelDisplay(): string {
    if (!this.playerForm || !this.player) return '—';
    const vipLevel = this.playerForm.get('vipLevel')?.value || this.player.vipLevel;
    if (!vipLevel) return '—';
    const vipLevelKey = `players.documents.general_data.vip_levels.${vipLevel}`;
    const translated = this._translocoService.translate(vipLevelKey);
    return translated === vipLevelKey ? vipLevel : translated;
  }

  getKycStatus(): string {
    if (!this.player) return '—';
    
    try {
      // Check if the player has a KYC status field
      const kycStatus = this.player.kycStatus;
      if (!kycStatus) return '—';
      
      // Map the KYC status value to the appropriate translation key
      const kycStatusKey = `players.documents.general_data.kyc_statuses.${kycStatus}`;
      const translated = this._translocoService.translate(kycStatusKey);
      return translated === kycStatusKey ? kycStatus : translated;
    } catch (error) {
      console.error('Error getting KYC status:', error);
      return '—';
    }
  }

  getAffiliateDisplay(): string {
    if (!this.playerForm) return '—';
    const affiliateId = this.playerForm.get('affiliateId')?.value;
    if (!affiliateId) return '—';
    
    const affiliate = AFFILIATES.find(a => a.id === affiliateId.toString());
    if (affiliate) {
      return `${affiliate.name} (${affiliateId})`;
    }
    return affiliateId.toString();
  }

  getProductDisplay(): string {
    if (!this.player) return '—';
    
    try {
      // Check if the player has a product field
      const product = this.player.product;
      if (!product) return '—';
      
      // Map the product value to the appropriate translation key
      let translationKey: string;
      switch (product.toUpperCase()) {
        case 'CASINO':
          translationKey = 'players.register-sessions.casino';
          break;
        case 'SPORTS':
        case 'SPORTS_BOOK':
          translationKey = 'players.register-sessions.sports';
          break;
        default:
          // If it's an unknown product, try to translate it using the bonus products translations
          translationKey = `players.bonus.options.products.${product.toUpperCase()}`;
      }
      
      const translated = this._translocoService.translate(translationKey);
      return translated === translationKey ? product : translated;
    } catch (error) {
      console.error('Error getting product display:', error);
      return '—';
    }
  }

  onDateOfBirthChange(date: Date | null): void {
    if (date) {
      // Remove time component, keep only date
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      this.playerForm.get('dateOfBirth')?.setValue(dateOnly);
    } else {
      this.playerForm.get('dateOfBirth')?.setValue(null);
    }
  }

  private parseDate(dateString: string | null | undefined): Date | null {
    if (!dateString) return null;
    
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? null : date;
      }
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }
} 